import React, { useState, useMemo } from 'react';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import type { KpiDrilldownSpec } from '../kpiDashboardApi';
import KpiChartPanel from './KpiChartPanel';
import { heatCellStyleIncidentBlue } from '../KpiCustomerHeatmapTable';
import {
  KPI_DASHBOARD_TABLE_TH_STICKY_FIRST_COL,
  KPI_DASHBOARD_TABLE_TH_STICKY_TOP,
  kpiDashboardTableRowClass,
} from '../kpiDashboardTableStyles';

export interface ProblemMatrixColumn {
  label: string;
  key: string;
}

export interface KpiProblemMatrixTableProps {
  title: string;
  columns: ProblemMatrixColumn[];
  rows: Array<{ customerId: string; customerName: string; counts: number[] }>;
  maxCount: number;
  drillMode: 'state' | 'week';
  onCellDrill: (spec: KpiDrilldownSpec) => void;
  emptyMessage?: string;
}

const KpiProblemMatrixTable: React.FC<KpiProblemMatrixTableProps> = ({
  title,
  columns,
  rows,
  maxCount,
  drillMode,
  onCellDrill,
  emptyMessage = 'No problems for the selected filters.',
}) => {
  const [sortDesc, setSortDesc] = useState(false);
  const maxH = maxCount > 0 ? maxCount : 1;

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' });
      return sortDesc ? -cmp : cmp;
    });
    return copy;
  }, [rows, sortDesc]);

  return (
    <KpiChartPanel
      title={title}
      flexFill
      className="min-h-0"
      bodyClassName="flex min-h-0 flex-1 flex-col px-0 pb-0 pt-0"
    >
      <div className="max-h-[min(22rem,65vh)] min-h-0 overflow-auto">
        <table className="w-max min-w-full border-collapse text-left text-xs">
          <thead>
            <tr>
              <th className={KPI_DASHBOARD_TABLE_TH_STICKY_FIRST_COL} scope="col">
                <button
                  type="button"
                  onClick={() => setSortDesc((d) => !d)}
                  className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-left font-semibold text-slate-800 hover:bg-slate-200/60"
                >
                  Company
                  <ArrowsUpDownIcon className="h-3.5 w-3.5 text-slate-600" aria-hidden />
                </button>
              </th>
              {columns.map((col) => (
                <th key={col.key} className={`${KPI_DASHBOARD_TABLE_TH_STICKY_TOP} whitespace-nowrap`} scope="col">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={Math.max(1, columns.length + 1)}
                  className="border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedRows.map((row, ri) => (
                <tr key={row.customerId} className={`border-b border-slate-200 ${kpiDashboardTableRowClass(ri)}`}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 min-w-[11rem] max-w-[15rem] border border-slate-200 bg-inherit px-3 py-2 text-left font-medium text-slate-900 shadow-[2px_0_4px_-2px_rgba(15,23,42,0.08)]"
                  >
                    <span className="line-clamp-2">{row.customerName}</span>
                  </th>
                  {row.counts.map((c, i) => {
                    const col = columns[i];
                    const clickable = c > 0 && col;
                    const drillSpec: KpiDrilldownSpec | null =
                      drillMode === 'state'
                        ? { kind: 'problem_state_customer', customerId: row.customerId, stateKey: col.key }
                        : { kind: 'problem_week_customer', customerId: row.customerId, weekStart: col.key };
                    return (
                      <td
                        key={`${row.customerId}-${col.key}`}
                        className={`border border-slate-200 px-1 py-1 text-center tabular-nums text-slate-800 ${
                          clickable ? 'cursor-pointer hover:ring-2 hover:ring-inset hover:ring-[#4472C4]/50' : ''
                        }`}
                        style={heatCellStyleIncidentBlue(c, maxH)}
                        onClick={clickable ? () => onCellDrill(drillSpec!) : undefined}
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onCellDrill(drillSpec!);
                                }
                              }
                            : undefined
                        }
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        aria-label={
                          clickable
                            ? `Open problems for ${row.customerName}, ${col.label}, count ${c}`
                            : undefined
                        }
                      >
                        <span className="inline-block min-w-[1.5rem]">{c > 0 ? c : ''}</span>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </KpiChartPanel>
  );
};

export default KpiProblemMatrixTable;
